import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { businessDate, businessDateTimeToDate, addBusinessDays, moveBusinessMonth } from '../lib/businessTime';
import { registerActivityEditNavigationGuard } from '../lib/activityEditGuard';
import ActivityEditHistoryDrawer from '../components/ActivityEditHistoryDrawer';

const money=n=>new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(Number(n||0));
const shortDate=value=>new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',month:'short',day:'numeric'}).format(value instanceof Date?value:new Date(value));
const editableOutboundFields=[['dms','prospect_contacted'],['responses','response'],['conversations','conversation_started']];
const eventTypeByField=Object.fromEntries(editableOutboundFields);
const ACTIVITY_MODE_KEY='leadTracker.activity.mode';
const ACTIVITY_EDIT_SESSION_KEY='leadTracker.activity.editSession';
const ESCAPE_EXIT_TARGET='__activity_escape__';
const validActivityModes=new Set(['day','week','month','custom']);
const validActivityMotions=new Set(['all','outbound','inbound']);

function storedActivityMode(){
  try{
    const value=sessionStorage.getItem(ACTIVITY_MODE_KEY);
    return validActivityModes.has(value)?value:'day';
  }catch{return 'day';}
}
function activityEditSessionStorageKey(workspaceId){return `${ACTIVITY_EDIT_SESSION_KEY}:${workspaceId}`;}
function storedActivityEditSession(workspaceId){
  if(!workspaceId)return null;
  try{return sessionStorage.getItem(activityEditSessionStorageKey(workspaceId))||null;}catch{return null;}
}
function storeActivityEditSession(workspaceId,sessionId){
  if(!workspaceId||!sessionId)return;
  try{sessionStorage.setItem(activityEditSessionStorageKey(workspaceId),sessionId);}catch{}
}
function clearStoredActivityEditSession(workspaceId,sessionId=null){
  if(!workspaceId)return;
  try{
    const key=activityEditSessionStorageKey(workspaceId);
    if(!sessionId||sessionStorage.getItem(key)===sessionId)sessionStorage.removeItem(key);
  }catch{}
}
function isReloadNavigation(){
  try{return performance.getEntriesByType('navigation')?.[0]?.type==='reload';}catch{return false;}
}
function monthStart(dateString){return `${dateString.slice(0,7)}-01`;}
function weekStart(dateString){
  const [y,m,d]=dateString.split('-').map(Number);
  const noon=new Date(Date.UTC(y,m-1,d,12));
  const mondayOffset=(noon.getUTCDay()+6)%7;
  return addBusinessDays(dateString,-mondayOffset);
}
function dayDiff(a,b){
  const [ay,am,ad]=a.split('-').map(Number),[by,bm,bd]=b.split('-').map(Number);
  return Math.round((Date.UTC(by,bm-1,bd)-Date.UTC(ay,am-1,ad))/86400000);
}
function labelForBucket(start,endExclusive,granularity){
  if(granularity==='day')return shortDate(businessDateTimeToDate(start,12));
  if(granularity==='month')return new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',month:'short',year:'numeric'}).format(businessDateTimeToDate(start,12));
  return `${shortDate(businessDateTimeToDate(start,12))}–${shortDate(businessDateTimeToDate(addBusinessDays(endExclusive,-1),12))}`;
}
function buildBuckets(mode,anchor,customStart,customEnd){
  if(mode==='day'){
    const end=addBusinessDays(anchor,1),start=addBusinessDays(end,-14),out=[];
    for(let d=start;d<end;d=addBusinessDays(d,1))out.push({start:d,end:addBusinessDays(d,1),granularity:'day'});
    return out.reverse();
  }
  if(mode==='week'){
    const current=weekStart(anchor),out=[];
    for(let i=0;i<8;i+=1){const start=addBusinessDays(current,-7*i);out.push({start,end:addBusinessDays(start,7),granularity:'week'});}
    return out;
  }
  if(mode==='month'){
    const current=monthStart(anchor),out=[];
    for(let i=0;i<12;i+=1){const start=moveBusinessMonth(current,-i);out.push({start,end:moveBusinessMonth(start,1),granularity:'month'});}
    return out;
  }
  const span=dayDiff(customStart,customEnd)+1;
  const granularity=span<=31?'day':span<=180?'week':'month';
  const out=[];
  if(granularity==='day'){
    for(let d=customStart;d<=customEnd;d=addBusinessDays(d,1))out.push({start:d,end:addBusinessDays(d,1),granularity});
  }else if(granularity==='week'){
    for(let d=weekStart(customStart);d<=customEnd;d=addBusinessDays(d,7))out.push({start:d,end:addBusinessDays(d,7),granularity});
  }else{
    for(let d=monthStart(customStart);d<=customEnd;d=moveBusinessMonth(d,1))out.push({start:d,end:moveBusinessMonth(d,1),granularity});
  }
  return out.reverse();
}
function rowSnapshot(rows){return Object.fromEntries(rows.map(r=>[r.key,{dms:String(Number(r.dms||0)),responses:String(Number(r.responses||0)),conversations:String(Number(r.conversations||0))}]));}
function newEditSessionId(){
  if(globalThis.crypto?.randomUUID)return globalThis.crypto.randomUUID();
  return `activity-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function newRequestId(){
  if(globalThis.crypto?.randomUUID)return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function ActivityPage(){
  const {workspace,user}=useAuth();
  const navigate=useNavigate();
  const today=businessDate();
  const [searchParams,setSearchParams]=useSearchParams();
  const launchedMode=validActivityModes.has(searchParams.get('mode'))?searchParams.get('mode'):null;
  const launchedMotion=validActivityMotions.has(searchParams.get('motion'))?searchParams.get('motion'):null;
  const launchedEdit=searchParams.get('edit')==='1';
  const [mode,setMode]=useState(()=>launchedMode||'day'),[anchor,setAnchor]=useState(today),[motion,setMotion]=useState(launchedMotion||'all');
  const [customStart,setCustomStart]=useState(addBusinessDays(today,-30)),[customEnd,setCustomEnd]=useState(today);
  const [events,setEvents]=useState([]),[loading,setLoading]=useState(true),[error,setError]=useState('');
  const [editMode,setEditMode]=useState(false),[editLock,setEditLock]=useState(null),[acquiringEdit,setAcquiringEdit]=useState(false),[sessionAvailable,setSessionAvailable]=useState(true);
  const [drafts,setDrafts]=useState({}),[baseline,setBaseline]=useState({}),[saving,setSaving]=useState(false),[stepSaving,setStepSaving]=useState({});
  const [focusCell,setFocusCell]=useState(null),[activeCell,setActiveCell]=useState(null);
  const [undoStack,setUndoStack]=useState([]),[redoStack,setRedoStack]=useState([]);
  const [sessionChanged,setSessionChanged]=useState(false),[pendingNavigation,setPendingNavigation]=useState(null);
  const [toast,setToast]=useState(null),[historyOpen,setHistoryOpen]=useState(false);
  const draftsRef=useRef({}),baselineRef=useRef({}),sessionChangedRef=useRef(false),saveChainRef=useRef(Promise.resolve()),editSessionIdRef=useRef(null),savingRef=useRef(false),sessionAvailableRef=useRef(true),launchedEditHandledRef=useRef(false),stepQueuesRef=useRef({});
  const stepSavingCount=Object.keys(stepSaving).length;
  const anySaving=saving||stepSavingCount>0;
  const buckets=useMemo(()=>buildBuckets(mode,anchor,customStart,customEnd),[mode,anchor,customStart,customEnd]);
  const periodRangeLabel=useMemo(()=>{
    if(!buckets.length)return '';
    const starts=buckets.map(b=>b.start).sort();
    const ends=buckets.map(b=>b.end).sort();
    const start=starts[0];
    const endInclusive=addBusinessDays(ends[ends.length-1],-1);
    return `${shortDate(businessDateTimeToDate(start,12))} – ${shortDate(businessDateTimeToDate(endInclusive,12))}`;
  },[buckets]);
  const queryRange=useMemo(()=>{
    const starts=buckets.map(b=>b.start).sort(),ends=buckets.map(b=>b.end).sort();
    return {start:businessDateTimeToDate(starts[0],0),end:businessDateTimeToDate(ends[ends.length-1],0)};
  },[buckets]);

  useEffect(()=>{savingRef.current=anySaving;},[anySaving]);
  useEffect(()=>{sessionAvailableRef.current=sessionAvailable;},[sessionAvailable]);
  useEffect(()=>{try{sessionStorage.setItem(ACTIVITY_MODE_KEY,mode);}catch{}},[mode]);
  useEffect(()=>{if(launchedEdit)setSearchParams({}, {replace:true});},[]);
  useEffect(()=>{
    if(!toast)return undefined;
    const id=window.setTimeout(()=>setToast(null),3200);
    return ()=>window.clearTimeout(id);
  },[toast]);
  useEffect(()=>{
    if(!editMode)return undefined;
    const onBeforeUnload=event=>{
      if(!hasDirtyDrafts()&&!savingRef.current)return;
      event.preventDefault();event.returnValue='';
    };
    window.addEventListener('beforeunload',onBeforeUnload);
    return ()=>window.removeEventListener('beforeunload',onBeforeUnload);
  },[editMode]);

  const load=useCallback(async({silent=false}={})=>{
    if(!silent)setLoading(true);
    setError('');
    const {data,error:e}=await supabase.from('activity_events')
      .select('id,event_type,motion,numeric_value,occurred_at,lead_id,leads!activity_events_workspace_id_lead_id_fkey(id,validity)')
      .eq('workspace_id',workspace.id)
      .gte('occurred_at',queryRange.start.toISOString()).lt('occurred_at',queryRange.end.toISOString())
      .order('occurred_at',{ascending:false});
    if(e)setError(e.message);else setEvents((data||[]).filter(x=>!x.lead_id||x.leads?.validity==='valid'));
    if(!silent)setLoading(false);
  },[workspace.id,queryRange.start.getTime(),queryRange.end.getTime()]);

  const refreshEditLock=useCallback(async()=>{
    if(!workspace?.id)return null;
    const {data,error:lockError}=await supabase.rpc('get_activity_edit_lock',{p_workspace_id:workspace.id});
    if(lockError)return null;
    setEditLock(data||null);
    return data||null;
  },[workspace?.id]);

  useEffect(()=>{load()},[load]);
  useEffect(()=>{
    if(!workspace?.id||editMode)return undefined;
    refreshEditLock();
    const id=window.setInterval(refreshEditLock,15000);
    return ()=>window.clearInterval(id);
  },[workspace?.id,editMode,refreshEditLock]);

  useEffect(()=>{
    if(!workspace?.id||editMode)return undefined;
    const channel=supabase.channel(`activity-ledger-${workspace.id}`)
      .on('postgres_changes',{event:'*',schema:'public',table:'activity_events',filter:`workspace_id=eq.${workspace.id}`},()=>load({silent:true}))
      .subscribe();
    const refresh=()=>{load({silent:true});refreshEditLock();};
    const onVisibility=()=>{if(document.visibilityState==='visible')refresh();};
    window.addEventListener('focus',refresh);document.addEventListener('visibilitychange',onVisibility);
    return ()=>{window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',onVisibility);supabase.removeChannel(channel);};
  },[workspace?.id,editMode,load,refreshEditLock]);

  const rows=useMemo(()=>buckets.map(bucket=>{
    const start=businessDateTimeToDate(bucket.start,0).getTime(),end=businessDateTimeToDate(bucket.end,0).getTime();
    const inBucket=events.filter(e=>{const t=new Date(e.occurred_at).getTime();return t>=start&&t<end&&(motion==='all'||e.motion===motion);});
    const count=type=>inBucket.filter(e=>e.event_type===type).reduce((s,e)=>s+Number(e.numeric_value||0),0);
    const acquisitionCount=(type,eventMotion='outbound')=>events.filter(e=>{const t=new Date(e.occurred_at).getTime();return t>=start&&t<end&&e.motion===eventMotion&&e.event_type===type;}).reduce((s,e)=>s+Number(e.numeric_value||0),0);
    return {key:bucket.start,label:labelForBucket(bucket.start,bucket.end,bucket.granularity),dms:motion==='inbound'?0:acquisitionCount('prospect_contacted'),responses:motion==='inbound'?count('response'):acquisitionCount('response'),conversations:motion==='inbound'?count('conversation_started'):acquisitionCount('conversation_started'),leads:count('lead_identified'),offered:count('offered'),forms:count('form_submitted'),qualified:count('qualified'),booked:count('call_booked'),showed:count('showed'),noShow:count('no_show'),closed:count('closed_won'),revenue:count('payment_received')};
  }),[buckets,events,motion]);

  useEffect(()=>{
    if(!editMode||mode!=='day'||motion!=='outbound'||loading||Object.keys(draftsRef.current).length>0)return;
    const snapshot=rowSnapshot(rows);draftsRef.current=snapshot;baselineRef.current=snapshot;setDrafts(snapshot);setBaseline(snapshot);
  },[editMode,mode,motion,loading,rows]);
  useEffect(()=>{
    if(!editMode||!focusCell)return;
    const id=window.setTimeout(()=>{const input=document.querySelector(`[data-activity-cell="${focusCell.date}:${focusCell.field}"]`);if(input){input.focus();input.select();}setFocusCell(null);},0);
    return ()=>window.clearTimeout(id);
  },[editMode,focusCell,drafts]);

  useEffect(()=>{
    if(!editMode||!editSessionIdRef.current)return undefined;
    let cancelled=false;
    const heartbeat=async()=>{
      const {data,error:heartbeatError}=await supabase.rpc('heartbeat_activity_edit_lock',{p_workspace_id:workspace.id,p_session_id:editSessionIdRef.current});
      if(cancelled)return;
      if(heartbeatError){
        if(sessionAvailableRef.current)setToast({type:'error',title:'Connection unavailable',detail:'Activity edits are paused until the edit lock can be verified.'});
        setSessionAvailable(false);return;
      }
      if(!data){
        resetEditSession();setToast({type:'error',title:'Activity editing moved',detail:'This tab is now read-only because your Activity edit lease moved to another tab or browser.'});
        refreshEditLock();return;
      }
      setSessionAvailable(true);
    };
    const id=window.setInterval(heartbeat,25000);
    return ()=>{cancelled=true;window.clearInterval(id);};
  },[editMode,workspace.id,refreshEditLock]);

  useEffect(()=>{
    if(launchedEditHandledRef.current||loading||!workspace?.id)return;
    const restoredSessionId=isReloadNavigation()?storedActivityEditSession(workspace.id):null;
    if(!launchedEdit&&!restoredSessionId)return;
    launchedEditHandledRef.current=true;
    if(restoredSessionId)recoverEditing(restoredSessionId);
    else startEditing();
  },[launchedEdit,loading,workspace?.id]);

  function hasDirtyDrafts(){return Object.entries(draftsRef.current).some(([date,values])=>editableOutboundFields.some(([field])=>String(values?.[field]??'')!==String(baselineRef.current?.[date]?.[field]??'')));}
  function resetEditSession(){
    const sessionId=editSessionIdRef.current;
    if(sessionId)clearStoredActivityEditSession(workspace.id,sessionId);
    stepQueuesRef.current={};
    setEditMode(false);setDrafts({});setBaseline({});setFocusCell(null);setActiveCell(null);setStepSaving({});
    setUndoStack([]);setRedoStack([]);setSessionChanged(false);setPendingNavigation(null);setError('');setSessionAvailable(true);
    draftsRef.current={};baselineRef.current={};sessionChangedRef.current=false;editSessionIdRef.current=null;
  }
  async function releaseEditLock(sessionId=editSessionIdRef.current){
    if(!sessionId)return true;
    const {data,error:releaseError}=await supabase.rpc('release_activity_edit_lock',{p_workspace_id:workspace.id,p_session_id:sessionId});
    if(releaseError)return false;
    clearStoredActivityEditSession(workspace.id,sessionId);
    setEditLock(null);return Boolean(data);
  }
  function enterOwnedEditSession(sessionId,data,targetCell=null){
    const snapshot=rowSnapshot(rows);
    storeActivityEditSession(workspace.id,sessionId);
    stepQueuesRef.current={};draftsRef.current=snapshot;baselineRef.current=snapshot;editSessionIdRef.current=sessionId;sessionAvailableRef.current=true;
    setHistoryOpen(false);setEditLock({...data,is_self:true});setDrafts(snapshot);setBaseline(snapshot);setUndoStack([]);setRedoStack([]);
    setSessionChanged(false);sessionChangedRef.current=false;setSessionAvailable(true);setFocusCell(targetCell);setEditMode(true);
  }
  async function recoverEditing(previousSessionId){
    if(acquiringEdit||editMode)return false;
    setAcquiringEdit(true);setError('');
    const sessionId=newEditSessionId();
    const {data,error:lockError}=await supabase.rpc('reclaim_activity_edit_lock',{p_workspace_id:workspace.id,p_previous_session_id:previousSessionId,p_new_session_id:sessionId});
    setAcquiringEdit(false);
    clearStoredActivityEditSession(workspace.id,previousSessionId);
    if(lockError){
      setToast({type:'error',title:'Edit Mode unavailable',detail:lockError.message||'Your Activity edit ownership could not be recovered.'});return false;
    }
    if(!data?.acquired){
      setEditLock(data||null);
      setToast({type:'error',title:data?.is_self?'Activity is open elsewhere':'Activity is being edited',detail:data?.is_self?'Your account already has Activity Edit Mode active in another tab or browser.':`${data?.display_name||'Another team member'} currently owns Edit Mode.`});
      return false;
    }
    enterOwnedEditSession(sessionId,data);return true;
  }
  async function takeoverEditing(){
    if(acquiringEdit||editMode||!editLock?.is_self)return false;
    setAcquiringEdit(true);setError('');
    const sessionId=newEditSessionId();
    const {data,error:lockError}=await supabase.rpc('takeover_activity_edit_lock',{p_workspace_id:workspace.id,p_new_session_id:sessionId});
    setAcquiringEdit(false);
    if(lockError){setToast({type:'error',title:'Couldn’t resume editing',detail:lockError.message||'Activity Edit Mode could not be moved to this tab.'});return false;}
    if(!data?.acquired){
      setEditLock(data||null);
      setToast({type:'error',title:'Activity is being edited',detail:`${data?.display_name||'Another team member'} currently owns Edit Mode.`});
      return false;
    }
    enterOwnedEditSession(sessionId,data);
    setToast({type:'success',title:'Editing resumed here',detail:'The previous Activity editing tab is now read-only.'});
    return true;
  }
  async function startEditing(targetCell=null){
    if(acquiringEdit||editMode)return false;
    setAcquiringEdit(true);setError('');
    const sessionId=newEditSessionId();
    const {data,error:lockError}=await supabase.rpc('acquire_activity_edit_lock',{p_workspace_id:workspace.id,p_session_id:sessionId});
    setAcquiringEdit(false);
    if(lockError){setToast({type:'error',title:'Edit Mode unavailable',detail:lockError.message||'The Activity edit lock could not be acquired.'});return false;}
    if(!data?.acquired){
      setEditLock(data||null);
      setToast({type:'error',title:data?.is_self?'Activity is open elsewhere':'Activity is being edited',detail:data?.is_self?'Your account already has Activity Edit Mode active in another tab or browser.':`${data?.display_name||'Another team member'} currently owns Edit Mode.`});return false;
    }
    enterOwnedEditSession(sessionId,data,targetCell);return true;
  }
  function updateDraft(date,field,value){
    if(value!==''&&!/^\d+$/.test(value))return;
    const next={...draftsRef.current,[date]:{...(draftsRef.current[date]||{}),[field]:value}};draftsRef.current=next;setDrafts(next);
  }
  function revertActiveCell(){
    if(!activeCell)return;
    const {date,field}=activeCell,value=String(baselineRef.current?.[date]?.[field]??'0');
    const next={...draftsRef.current,[date]:{...(draftsRef.current[date]||{}),[field]:value}};draftsRef.current=next;setDrafts(next);setActiveCell(null);
  }
  function setCommittedValue(date,field,value){
    const stringValue=String(value??0);
    const nextBaseline={...baselineRef.current,[date]:{...(baselineRef.current[date]||{}),[field]:stringValue}};
    const nextDrafts={...draftsRef.current,[date]:{...(draftsRef.current[date]||{}),[field]:stringValue}};
    baselineRef.current=nextBaseline;draftsRef.current=nextDrafts;setBaseline(nextBaseline);setDrafts(nextDrafts);
  }
  function activityChange(date,field,total,{actionKind='edit',saveMethod='autosave',expectedPrevious}={}){
    return {date,event_type:eventTypeByField[field],total,expected_previous_total:expectedPrevious,action_kind:actionKind,save_method:saveMethod,edit_session_id:editSessionIdRef.current};
  }
  function handleWriteError(err,date,field,previous){
    const message=err?.message||'Activity changes could not be saved.';
    const conflict=message.match(/Current value is ([0-9]+(?:\.[0-9]+)?)/i);
    if(conflict)setCommittedValue(date,field,Number(conflict[1]));
    else if(date&&field)setCommittedValue(date,field,previous);
    setToast({type:'error',title:message.includes('changed elsewhere')?'Activity changed elsewhere':'Activity wasn’t updated',detail:message});
  }

  function saveCell(date,field,{showToast=false,recordHistory=true,saveMethod='autosave'}={}){
    const raw=draftsRef.current?.[date]?.[field],previous=Number(baselineRef.current?.[date]?.[field]??0);
    const target=raw===''||raw===null||raw===undefined?0:Number(raw);
    if(!Number.isInteger(target)||target<0||target>100000){setToast({type:'error',title:'Activity wasn’t updated',detail:'Activity totals must be whole numbers between 0 and 100000.'});return Promise.resolve(false);}
    if(target===previous){setCommittedValue(date,field,target);setActiveCell(null);return Promise.resolve(true);}
    if(!sessionAvailableRef.current){setCommittedValue(date,field,previous);setToast({type:'error',title:'Activity wasn’t updated',detail:'The edit lock cannot currently be verified.'});return Promise.resolve(false);}

    setSaving(true);setError('');
    const operation=saveChainRef.current.then(async()=>{
      const {error:rpcError}=await supabase.rpc('set_outbound_activity_totals',{p_workspace_id:workspace.id,p_changes:[activityChange(date,field,target,{saveMethod,expectedPrevious:previous})]});
      if(rpcError)throw rpcError;
      setCommittedValue(date,field,target);sessionChangedRef.current=true;setSessionChanged(true);
      if(recordHistory){setUndoStack(current=>[...current,{date,field,from:previous,to:target}]);setRedoStack([]);}
      setActiveCell(null);if(showToast)setToast({type:'success',title:'Changes saved',detail:'Activity updated successfully'});return true;
    }).catch(err=>{handleWriteError(err,date,field,previous);return false;}).finally(()=>setSaving(false));
    saveChainRef.current=operation.then(()=>undefined,()=>undefined);return operation;
  }

  function updateStepSaving(cellKey,pending){
    setStepSaving(current=>{
      if(pending>0)return {...current,[cellKey]:pending};
      const next={...current};delete next[cellKey];return next;
    });
  }
  async function processStepQueue(date,field,cellKey){
    const queue=stepQueuesRef.current[cellKey];
    if(!queue||queue.running)return;
    queue.running=true;
    const dirty=String(draftsRef.current?.[date]?.[field]??'')!==String(baselineRef.current?.[date]?.[field]??'');
    if(dirty){
      const saved=await saveCell(date,field);
      if(!saved){queue.items=[];queue.running=false;queue.currentDelta=0;delete stepQueuesRef.current[cellKey];updateStepSaving(cellKey,0);return;}
    }
    while(queue.items.length&&sessionAvailableRef.current){
      const item=queue.items.shift();
      queue.currentDelta=item.delta;
      updateStepSaving(cellKey,queue.items.length+1);
      const previous=Number(baselineRef.current?.[date]?.[field]??0);
      const {data,error:rpcError}=await supabase.rpc('adjust_outbound_activity_total',{p_workspace_id:workspace.id,p_business_date:date,p_event_type:eventTypeByField[field],p_delta:item.delta,p_edit_session_id:editSessionIdRef.current,p_request_id:item.requestId,p_action_kind:'edit',p_save_method:'stepper'});
      if(rpcError){
        queue.items=[];queue.currentDelta=0;handleWriteError(rpcError,date,field,previous);break;
      }
      const from=Number(data?.previous_total??previous),to=Number(data?.corrected_total??from+item.delta);
      setCommittedValue(date,field,to);sessionChangedRef.current=true;setSessionChanged(true);
      setUndoStack(current=>[...current,{date,field,from,to}]);setRedoStack([]);
      queue.currentDelta=0;
    }
    queue.running=false;queue.currentDelta=0;
    if(queue.items.length===0)delete stepQueuesRef.current[cellKey];
    updateStepSaving(cellKey,0);
  }
  function stepCell(date,field,delta){
    if(!sessionAvailableRef.current)return;
    const cellKey=`${date}:${field}`;
    let queue=stepQueuesRef.current[cellKey];
    if(!queue){queue={running:false,currentDelta:0,items:[]};stepQueuesRef.current[cellKey]=queue;}
    const projected=Number(baselineRef.current?.[date]?.[field]??0)+(queue.currentDelta||0)+queue.items.reduce((sum,item)=>sum+item.delta,0);
    if(delta<0&&projected<=0)return;
    queue.items.push({delta,requestId:newRequestId()});
    updateStepSaving(cellKey,queue.items.length+(queue.running?1:0));setError('');
    processStepQueue(date,field,cellKey);
  }

  async function saveChangesAndExit({refresh=true,saveMethod='save_changes'}={}){
    if(anySaving)return false;
    if(activeCell&&hasDirtyDrafts()){const saved=await saveCell(activeCell.date,activeCell.field,{saveMethod});if(!saved)return false;}
    await saveChainRef.current.catch(()=>{});if(hasDirtyDrafts())return false;
    const sessionId=editSessionIdRef.current;await releaseEditLock(sessionId);resetEditSession();if(refresh)await load({silent:true});await refreshEditLock();return true;
  }
  async function discardChangesAndExit({refresh=true}={}){
    if(anySaving||!sessionAvailableRef.current)return false;
    setSaving(true);setError('');
    try{
      await saveChainRef.current.catch(()=>{});
      const changes=[...undoStack].reverse();
      for(const change of changes){
        const {error:rpcError}=await supabase.rpc('set_outbound_activity_totals',{p_workspace_id:workspace.id,p_changes:[activityChange(change.date,change.field,change.from,{actionKind:'undo',saveMethod:'discard',expectedPrevious:change.to})]});
        if(rpcError)throw {error:rpcError,change};
        setCommittedValue(change.date,change.field,change.from);
      }
      const sessionId=editSessionIdRef.current;
      const released=await releaseEditLock(sessionId);
      if(!released)throw {error:new Error('The Activity edit lock could not be released.')};
      resetEditSession();
      if(refresh)await load({silent:true});
      await refreshEditLock();
      return true;
    }catch(failure){
      const change=failure?.change;
      handleWriteError(failure?.error||failure,change?.date,change?.field,change?.to);
      return false;
    }finally{setSaving(false);}
  }
  async function undoChange(){
    if(!editMode||undoStack.length===0||anySaving||!sessionAvailableRef.current)return;
    revertActiveCell();const change=undoStack[undoStack.length-1];setSaving(true);setError('');
    const {error:rpcError}=await supabase.rpc('set_outbound_activity_totals',{p_workspace_id:workspace.id,p_changes:[activityChange(change.date,change.field,change.from,{actionKind:'undo',saveMethod:'keyboard',expectedPrevious:change.to})]});
    setSaving(false);if(rpcError){handleWriteError(rpcError,change.date,change.field,change.to);return;}
    setCommittedValue(change.date,change.field,change.from);setUndoStack(current=>current.slice(0,-1));setRedoStack(current=>[...current,change]);setToast({type:'success',title:'Change undone',detail:'Ctrl+Y to redo'});
  }
  async function redoChange(){
    if(!editMode||redoStack.length===0||anySaving||!sessionAvailableRef.current)return;
    revertActiveCell();const change=redoStack[redoStack.length-1];setSaving(true);setError('');
    const {error:rpcError}=await supabase.rpc('set_outbound_activity_totals',{p_workspace_id:workspace.id,p_changes:[activityChange(change.date,change.field,change.to,{actionKind:'redo',saveMethod:'keyboard',expectedPrevious:change.from})]});
    setSaving(false);if(rpcError){handleWriteError(rpcError,change.date,change.field,change.from);return;}
    setCommittedValue(change.date,change.field,change.to);setRedoStack(current=>current.slice(0,-1));setUndoStack(current=>[...current,change]);setToast({type:'success',title:'Change redone',detail:'Ctrl+Z to undo'});
  }

  useEffect(()=>{
    if(!editMode)return undefined;
    const onKeyDown=event=>{
      const key=event.key.toLowerCase();
      if((event.ctrlKey||event.metaKey)&&key==='z'&&!event.shiftKey){event.preventDefault();undoChange();}
      else if((event.ctrlKey||event.metaKey)&&(key==='y'||(key==='z'&&event.shiftKey))){event.preventDefault();redoChange();}
      else if(event.key==='Escape'){
        event.preventDefault();
        if(sessionChangedRef.current||hasDirtyDrafts()){setPendingNavigation(ESCAPE_EXIT_TARGET);return;}
        const sessionId=editSessionIdRef.current;releaseEditLock(sessionId);resetEditSession();refreshEditLock();
      }
    };
    document.addEventListener('keydown',onKeyDown);return ()=>document.removeEventListener('keydown',onKeyDown);
  },[editMode,undoStack,redoStack,anySaving,activeCell]);
  useEffect(()=>{
    if(!editMode)return undefined;
    return registerActivityEditNavigationGuard(to=>{
      if(to==='/activity')return true;
      if(!sessionChangedRef.current&&!hasDirtyDrafts()){
        const sessionId=editSessionIdRef.current;releaseEditLock(sessionId);resetEditSession();return true;
      }
      setPendingNavigation(to);return false;
    });
  },[editMode]);

  async function confirmNavigation(){const destination=pendingNavigation,isEscapeExit=destination===ESCAPE_EXIT_TARGET,saved=await saveChangesAndExit({refresh:isEscapeExit,saveMethod:'navigation'});if(saved&&destination&&!isEscapeExit)navigate(destination);}
  async function discardNavigation(){const destination=pendingNavigation,isEscapeExit=destination===ESCAPE_EXIT_TARGET,discarded=await discardChangesAndExit({refresh:isEscapeExit});if(discarded&&destination&&!isEscapeExit)navigate(destination);}
  function shiftWindow(delta){if(editMode)return;if(mode==='day')setAnchor(addBusinessDays(anchor,delta*14));if(mode==='week')setAnchor(addBusinessDays(anchor,delta*56));if(mode==='month')setAnchor(moveBusinessMonth(anchor,delta*12));}
  const canGoNewer=mode!=='custom'&&anchor<today;
  const periodTitle=mode==='day'?'Daily Activity':mode==='week'?'Weekly Activity':mode==='month'?'Monthly Activity':'Custom Activity';
  const firstHeader=mode==='day'?'Day':mode==='week'?'Week':mode==='month'?'Month':'Period';
  const motionLabel=motion==='all'?'All':motion==='inbound'?'Inbound':'Outbound';
  const editable=mode==='day'&&motion==='outbound';
  const lockOwner=editLock?.is_self?'you':editLock?.display_name||'another team member';
  const editStatus=!sessionAvailable?'Connection unavailable':anySaving?'Saving…':sessionChanged?'All changes saved':'Editing';
  const escapeExit=pendingNavigation===ESCAPE_EXIT_TARGET;

  const editableCell=(row,field)=>{
    const dirty=String(drafts[row.key]?.[field]??row[field]??0)!==String(baseline[row.key]?.[field]??row[field]??0);
    const cellKey=`${row.key}:${field}`,cellSaving=Boolean(stepSaving[cellKey]);
    return <td className={dirty?'activity-cell-changed':''}><div className={`activity-hover-stepper${cellSaving?' saving':''}`}>
      <button type="button" className="activity-step-button minus" disabled={!sessionAvailable||(!cellSaving&&Number(baseline[row.key]?.[field]??row[field]??0)<=0)} aria-label={`Decrease ${row.label} ${field}`} onMouseDown={e=>e.preventDefault()} onClick={()=>stepCell(row.key,field,-1)}>−</button>
      <input data-activity-cell={`${row.key}:${field}`} className="activity-inline-input" inputMode="numeric" value={drafts[row.key]?.[field]??String(row[field]??0)} disabled={cellSaving||!sessionAvailable} onFocus={()=>setActiveCell({date:row.key,field})} onChange={e=>updateDraft(row.key,field,e.target.value)} onBlur={()=>saveCell(row.key,field)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();saveCell(row.key,field,{showToast:true,saveMethod:'enter'});}else if(e.key==='Escape'){e.preventDefault();e.stopPropagation();if(sessionChangedRef.current||hasDirtyDrafts()){setPendingNavigation(ESCAPE_EXIT_TARGET);}else{const sessionId=editSessionIdRef.current;releaseEditLock(sessionId);resetEditSession();refreshEditLock();}}}} aria-label={`${row.label} ${field}`} />
      <button type="button" className="activity-step-button plus" disabled={!sessionAvailable} aria-label={`Increase ${row.label} ${field}`} onMouseDown={e=>e.preventDefault()} onClick={()=>stepCell(row.key,field,1)}>+</button>
    </div></td>;
  };
  const readOnlyEditableCell=(row,field)=><td className={`activity-double-click-cell${editLock?' locked':''}`} title={editLock?`Activity is currently being edited by ${lockOwner}`:'Double-click to edit'} onDoubleClick={()=>{if(!editLock)startEditing({date:row.key,field});}}>{row[field]??0}</td>;

  return <section className="page-stack activity-ledger-page">
    <div className="page-head-row"><div><h1 className="page-title">Activity</h1><p className="page-description">Business activity over time using the New York business day.</p></div><button className="secondary-button" disabled={editMode} onClick={()=>setHistoryOpen(true)}>Edit History</button></div>
    {!editMode&&editLock&&<div className="activity-lock-banner"><span className="activity-lock-icon" aria-hidden="true">🔒</span><div><strong>{editLock.is_self?'You already have Activity Edit Mode open':'Activity is currently being edited by '+lockOwner}</strong><span>{editLock.is_self?'Only one editing tab can be active at a time. Resume here to move your editing lease to this tab.':'You can continue viewing Activity. Edit Mode becomes available when that user finishes.'}</span></div>{editLock.is_self&&<button className="secondary-button inline-button" disabled={acquiringEdit} onClick={takeoverEditing}>{acquiringEdit?'Resuming…':'Resume editing here'}</button>}</div>}
    <div className="panel activity-controls">
      <div className="segmented">{[['day','Daily'],['week','Weekly'],['month','Monthly'],['custom','Custom']].map(([k,l])=><button key={k} disabled={editMode} className={mode===k?'active':''} onClick={()=>{setMode(k);setAnchor(today);}}>{l}</button>)}</div>
      {mode==='custom'?<div className="custom-range"><input className="text-input" type="date" disabled={editMode} max={today} value={customStart} onChange={e=>setCustomStart(e.target.value)}/><span>to</span><input className="text-input" type="date" disabled={editMode} max={today} min={customStart} value={customEnd} onChange={e=>setCustomEnd(e.target.value)}/></div>:<div className="period-nav"><button className="secondary-button inline-button" disabled={editMode} onClick={()=>shiftWindow(-1)}>← Older</button><span className="activity-period-range">{periodRangeLabel}</span><button className="secondary-button inline-button" disabled={editMode||!canGoNewer} onClick={()=>shiftWindow(1)}>Newer →</button></div>}
      <div className="segmented motion-filter">{[['all','All'],['outbound','Outbound'],['inbound','Inbound']].map(([k,l])=><button key={k} disabled={editMode} className={motion===k?'active':''} onClick={()=>setMotion(k)}>{l}</button>)}</div>
    </div>

    {error&&<div className="error-message">{error}</div>}
    <div className="panel table-panel activity-ledger-panel">
      <div className="activity-ledger-head"><h2>{periodTitle}</h2><div className="activity-ledger-actions">{editable&&!editMode&&<button className="secondary-button inline-button" disabled={Boolean(editLock)||acquiringEdit} onClick={()=>startEditing()}>{acquiringEdit?'Starting…':'Edit Activity'}</button>}{editMode&&<><span className="activity-edit-status">{editStatus}</span><button className="secondary-button inline-button" disabled={undoStack.length===0||anySaving||!sessionAvailable} onMouseDown={e=>e.preventDefault()} onClick={undoChange}>Undo</button><button className="secondary-button inline-button" disabled={redoStack.length===0||anySaving||!sessionAvailable} onMouseDown={e=>e.preventDefault()} onClick={redoChange}>Redo</button><button className="primary-button no-margin compact" disabled={anySaving||!sessionAvailable} onMouseDown={e=>e.preventDefault()} onClick={()=>saveChangesAndExit()}>Save Changes</button></>}<span className="pill">{motionLabel} · {mode==='custom'?'Custom':mode[0].toUpperCase()+mode.slice(1)+'ly'}</span></div></div>
      {editMode&&<div className="activity-edit-banner"><strong>Edit Mode</strong><span>{sessionAvailable?'Hover any editable value for − / +. Rapid clicks are queued and saved in order. Click a number to type directly.':'Editing is paused while the server verifies this edit session.'}</span></div>}
      {loading?<div className="empty-state">Loading activity…</div>:<div className="simple-table-wrap"><table className="simple-table activity-ledger-table"><thead><tr>
        <th>{firstHeader}</th>
        {motion==='inbound'?<><th>Leads</th><th>Responses</th><th>Conversations</th></>:<><th>Outbound DMs*</th><th>Responses</th><th>Conversations</th><th>Leads</th></>}
        <th>Offered</th><th>Forms</th><th>Qualified</th><th>Booked</th><th>Showed</th><th>No Show</th><th>Closed</th><th>Revenue</th>
      </tr></thead><tbody>{rows.map(r=><tr key={r.key}>
        <td>{r.label}</td>
        {motion==='inbound'?<><td>{r.leads}</td><td>{r.responses}</td><td>{r.conversations}</td></>:<>{editMode&&editable?editableCell(r,'dms'):editable?readOnlyEditableCell(r,'dms'):<td>{r.dms}</td>}{editMode&&editable?editableCell(r,'responses'):editable?readOnlyEditableCell(r,'responses'):<td>{r.responses}</td>}{editMode&&editable?editableCell(r,'conversations'):editable?readOnlyEditableCell(r,'conversations'):<td>{r.conversations}</td>}<td>{r.leads}</td></>}
        <td>{r.offered}</td><td>{r.forms}</td><td>{r.qualified}</td><td>{r.booked}</td><td>{r.showed}</td><td>{r.noShow}</td><td>{r.closed}</td><td className="activity-revenue">{money(r.revenue)}</td>
      </tr>)}</tbody></table></div>}
      <div className="activity-ledger-note">{editMode?'Daily outbound values are exclusively locked to your user-owned Activity edit session. Rapid − / + clicks are queued and server-confirmed in order; typed values remain conflict-checked.':editable?(editLock?`${editLock.is_self?'Your account already owns':'Activity editing is temporarily locked by '+lockOwner}. You can continue viewing the ledger.`:'Double-click an Outbound DM, Response or Conversation value to edit that cell. * Outbound DMs Sent is outbound-only.'):'* Outbound DMs Sent is outbound-only. Leads, Responses and Conversations remain separately tagged metrics in inbound; the initial inbound Response is recorded automatically with Lead creation.'}</div>
    </div>

    {pendingNavigation&&<div className="activity-exit-backdrop" role="presentation"><div className="activity-exit-dialog" role="dialog" aria-modal="true" aria-labelledby="activity-exit-title"><h2 id="activity-exit-title">{escapeExit?'Exit Edit Mode?':'Save changes?'}</h2><p>{escapeExit?'You have changes in this edit session. Save them before ending Edit Mode?':'You have changes in this edit session. Save them before leaving Activity?'} Undo/Redo history ends when Edit Mode ends.</p><p className="activity-exit-warning">Discarding will remove all changes made in this edit session.</p><div className="activity-exit-actions"><button className="secondary-button inline-button activity-discard-button" onClick={discardNavigation} disabled={anySaving||!sessionAvailable}>Discard changes</button><div className="activity-exit-safe-actions"><button className="secondary-button inline-button" onClick={()=>setPendingNavigation(null)} disabled={anySaving}>Stay in edit mode</button><button className="primary-button no-margin compact" onClick={confirmNavigation} disabled={anySaving||!sessionAvailable}>{escapeExit?'Save & exit':'Save & continue'}</button></div></div></div></div>}
    <ActivityEditHistoryDrawer open={historyOpen} onClose={()=>setHistoryOpen(false)} workspaceId={workspace.id} userId={user?.id}/>
    {toast&&<div className={`activity-save-toast${toast.type==='error'?' error':''}`} role="status" aria-live="polite"><span className="activity-save-toast-check">{toast.type==='error'?'!':'✓'}</span><span><strong>{toast.title}</strong><small>{toast.detail}</small></span></div>}
  </section>;
}